import XMarkIcon from '@heroicons/react/24/outline/XMarkIcon';
import { useLayoutEffect, useRef, useState } from 'react';

import { Badge } from '@/components/core/Badge';
import { useTranslation } from '@/hooks/useTranslation';
import { countFittingItems } from '@/pages/dam/utils/countFittingItems';
import type { DamStatusBadge } from '@/pages/dam/utils/damStatusBadges';
import { cn } from '@/services/utils';

/** Matches `tw-gap-2` of the row. */
const BADGE_GAP_PX = 8;

const badgeButtonClassName = cn(
    'tw-m-0 tw-inline-flex tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-transparent tw-p-0',
    'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2',
);

interface DamCardBadgeRowProps {
    badges: DamStatusBadge[];
}

const StatusBadge = ({ badge }: { badge: DamStatusBadge }) => (
    <Badge variant={badge.variant} size="xl" icon={badge.icon}>{badge.label}</Badge>
);

/** Leading badges that fit next to the "+N" badge. */
const countFittingBadges = (available: number, badgeWidths: number[], moreWidth: number): number =>
    countFittingItems(available, badgeWidths, moreWidth, BADGE_GAP_PX);

/**
 * Status badges below the card image: one line, the rest behind "+N". Expanded,
 * all badges wrap and a close badge collapses the row again.
 */
function DamCardBadgeRow({ badges }: DamCardBadgeRowProps) {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [visibleCount, setVisibleCount] = useState(badges.length);
    const rowRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const row = rowRef.current;
        const measure = measureRef.current;
        if (!row || !measure) {
            return undefined;
        }

        const update = () => {
            const children = Array.from(measure.children) as HTMLElement[];
            const moreWidth = children[children.length - 1]?.offsetWidth ?? 0;
            const badgeWidths = children.slice(0, -1).map((child) => child.offsetWidth);
            setVisibleCount(countFittingBadges(row.clientWidth, badgeWidths, moreWidth));
        };

        update();
        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(update);
        observer.observe(row);
        return () => observer.disconnect();
    }, [badges]);

    if (badges.length === 0) {
        return null;
    }

    const hiddenCount = badges.length - visibleCount;
    const shownBadges = isExpanded ? badges : badges.slice(0, visibleCount);

    return (
        <div ref={rowRef} className="tw-relative tw-min-w-0">
            <div ref={measureRef} aria-hidden className="tw-pointer-events-none tw-invisible tw-absolute tw-flex tw-gap-2 tw-whitespace-nowrap">
                {badges.map((badge, index) => <StatusBadge key={`${badge.label}-${index}`} badge={badge} />)}
                <Badge type="category" variant="white" size="xl">{t('DAM.CARD.MORE_BADGES', { count: badges.length })}</Badge>
            </div>
            <div className={cn('tw-flex tw-gap-2', isExpanded ? 'tw-flex-wrap' : 'tw-flex-nowrap tw-overflow-hidden')}>
                {shownBadges.map((badge, index) => <StatusBadge key={`${badge.label}-${index}`} badge={badge} />)}
                {!isExpanded && hiddenCount > 0 && (
                    <button
                        type="button"
                        className={badgeButtonClassName}
                        aria-expanded={false}
                        aria-label={t('DAM.CARD.SHOW_ALL_BADGES')}
                        onClick={() => setIsExpanded(true)}
                    >
                        <Badge type="category" variant="white" size="xl">{t('DAM.CARD.MORE_BADGES', { count: hiddenCount })}</Badge>
                    </button>
                )}
                {isExpanded && hiddenCount > 0 && (
                    <button
                        type="button"
                        className={badgeButtonClassName}
                        aria-expanded
                        aria-label={t('DAM.CARD.SHOW_FEWER_BADGES')}
                        onClick={() => setIsExpanded(false)}
                    >
                        <Badge variant="neutral" size="xl" icon={XMarkIcon} aria-label={t('DAM.CARD.SHOW_FEWER_BADGES')} />
                    </button>
                )}
            </div>
        </div>
    );
}

export { countFittingBadges, DamCardBadgeRow, StatusBadge };
export type { DamCardBadgeRowProps };
